import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../assets/contexts/AuthContext';
import { useTheme } from '../../assets/contexts/ThemeContext';
import { usePopup } from '../../assets/contexts/PopupContext';
import { usePageLayout } from '../../assets/contexts/PageLayoutContext';
import { resolveActiveTopMenuIndex } from '../../assets/utils/comm/activeTopMenuFromBreadcrumb';
import HeaderLanguageSwitcher from '../../sites/default/layouts/HeaderLanguageSwitcher';
import './mobile-menu-classic.css';
import './mobile-menu-modern.css';

function resolveMobileProfileAvatarSrc(u) {
  if (!u || typeof u !== 'object') return '';
  const fromFull = u.profileImage != null && String(u.profileImage).trim() !== '' ? String(u.profileImage).trim() : '';
  if (fromFull) return fromFull;
  const pu = u.profileUid;
  if (pu != null && String(pu).trim() !== '') {
    return `/api/glow/files/viewByUid/${encodeURIComponent(String(pu).trim())}`;
  }
  const pid = u.profileId;
  if (pid != null && String(pid).trim() !== '') {
    return `/api/glow/files/view?fileId=${encodeURIComponent(String(pid).trim())}`;
  }
  return '';
}

/**
 * 모바일 메뉴 컴포넌트
 * React 표준 방식으로 구현한 트리 노드형 메뉴
 * Slinky 플러그인 없이 순수 React로 구현
 */
const MobileMenu = ({ show, onClose, menuData = [] }) => {
  const { t } = useTranslation();
  const mobileMenuRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, logout } = useAuth();
  const { openConfirm } = usePopup();
  const themeContext = useTheme();
  const themeConfig = themeContext?.themeConfig || {};
  const { bannerData } = usePageLayout();
  const themeColor = themeConfig.primaryColor || themeConfig.themeColor || '#5ac8fa';
  const mobileMenuStyle = themeConfig.mobileMenuStyle || 'modern';

  const profileAvatarSrc = useMemo(() => (user ? resolveMobileProfileAvatarSrc(user) : ''), [user]);
  const [profileAvatarError, setProfileAvatarError] = useState(false);

  useEffect(() => {
    setProfileAvatarError(false);
  }, [profileAvatarSrc]);

  // 열린 메뉴 경로를 Set으로 관리 (예: ['0', '0-1'] = 첫 번째 메뉴의 두 번째 하위 메뉴)
  const [expandedPaths, setExpandedPaths] = useState(new Set());
  // 활성 메뉴 경로 저장 (활성 페이지와 그 부모 경로들)
  const [activeMenuPaths, setActiveMenuPaths] = useState(new Set());

  // show prop에 따라 inside 클래스 추가/제거
  useEffect(() => {
    if (mobileMenuRef.current) {
      if (show) {
        mobileMenuRef.current.classList.add('inside');
      } else {
        mobileMenuRef.current.classList.remove('inside');
        // 메뉴가 닫힐 때 열린 경로 초기화
        setExpandedPaths(new Set());
      }
    }
  }, [show]);

  // React Router 경로로 변환하는 헬퍼 함수
  const convertToReactRoute = useCallback((href) => {
    if (!href || href === '#') return null;
    if (/^(https?:)?\/\//.test(href)) return null; // 외부 URL
    return href;
  }, []);

  // 현재 경로에 해당하는 메뉴 경로 찾기 (재귀적) - 정확히 일치하는 항목만 찾기
  const findActiveMenuPath = useMemo(() => {
    const findPath = (items, currentPath, path = []) => {
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        const href = item.href && item.href !== '#' ? item.href : '#';
        const route = convertToReactRoute(href);

        // 정확히 일치하는 경우만 활성 메뉴로 인식
        if (route && route === currentPath) {
          return path.concat(i);
        }

        // 하위 메뉴에서 찾기
        if (item.children && Array.isArray(item.children) && item.children.length > 0) {
          const found = findPath(item.children, currentPath, path.concat(i));
          if (found) {
            return found;
          }
        }
      }
      return null;
    };
    return findPath;
  }, [convertToReactRoute]);

  // 하위 메뉴에 활성 항목이 있는지 확인하는 함수
  const hasActiveChild = useCallback((item, currentPath) => {
    if (!item.children || !Array.isArray(item.children) || item.children.length === 0) {
      return false;
    }
    for (const child of item.children) {
      const childHref = child.href && child.href !== '#' ? child.href : '#';
      const childRoute = convertToReactRoute(childHref);
      if (childRoute && childRoute === currentPath) {
        return true;
      }
      if (hasActiveChild(child, currentPath)) {
        return true;
      }
    }
    return false;
  }, [convertToReactRoute]);

  // 메뉴가 열릴 때 현재 경로에 해당하는 메뉴 경로를 자동으로 열기 (없으면 breadcrumb으로 1depth만)
  useEffect(() => {
    if (show && menuData && menuData.length > 0) {
      let activePath = findActiveMenuPath(menuData, location.pathname);
      if (!activePath?.length) {
        const topIdx = resolveActiveTopMenuIndex(menuData, location.pathname, bannerData?.breadcrumbPath);
        if (topIdx != null) activePath = [topIdx];
      }
      if (activePath && activePath.length > 0) {
        const pathsToExpand = new Set();
        const pathsToActivate = new Set();
        for (let i = 1; i <= activePath.length; i++) {
          const pathKey = activePath.slice(0, i).join('-');
          pathsToExpand.add(pathKey);
          pathsToActivate.add(pathKey);
        }
        setExpandedPaths(pathsToExpand);
        setActiveMenuPaths(pathsToActivate);
      } else {
        setActiveMenuPaths(new Set());
      }
    }
  }, [show, menuData, location.pathname, bannerData?.breadcrumbPath, findActiveMenuPath]);

  // 메뉴 항목 토글 (하위 메뉴 열기/닫기)
  const toggleMenu = useCallback((pathKey) => {
    setExpandedPaths(prev => {
      const newSet = new Set(prev);
      if (newSet.has(pathKey)) {
        newSet.delete(pathKey);
        // 하위 경로도 모두 제거
        Array.from(newSet).forEach(key => {
          if (key.startsWith(pathKey + '-')) {
            newSet.delete(key);
          }
        });
      } else {
        newSet.add(pathKey);
      }
      return newSet;
    });
  }, []);

  // 메뉴 항목 렌더링 (트리 구조)
  const renderMenuItem = useCallback((item, index, level = 0, parentPath = []) => {
    const hasChildren = item.children && Array.isArray(item.children) && item.children.length > 0;
    const href = item.href && item.href !== '#' ? item.href : '#';
    const route = convertToReactRoute(href);
    const currentPath = parentPath.concat(index);
    const pathKey = currentPath.join('-');
    const isExpanded = expandedPaths.has(pathKey);
    // 활성 메뉴 경로에 포함된 경우 활성화 (활성 페이지와 그 부모 경로들)
    const isActive = activeMenuPaths.has(pathKey);

    const handleMenuClick = (e) => {
      e.preventDefault();
      e.stopPropagation();

      if (hasChildren) {
        // 하위 메뉴가 있는 경우 토글
        toggleMenu(pathKey);
      } else if (route) {
        // 링크가 있는 경우 React Router로 이동
        navigate(route);
        if (onClose) {
          onClose();
        }
      } else if (href && href !== '#') {
        // 외부 링크
        window.location.href = href;
        if (onClose) {
          onClose();
        }
      }
    };

    return (
      <li
        key={pathKey}
        className={`mobile-menu-item ${hasChildren ? 'has-children' : ''} ${isExpanded ? 'expanded' : ''} ${isActive ? 'active' : ''}`}
      >
        <a
          href={href}
          className={`mobile-menu-link ${hasChildren ? 'has-arrow' : ''}`}
          onClick={handleMenuClick}
        >
          <span>{item.label}</span>
          {hasChildren ? (
            <i className={`mobile-menu-arrow ${isExpanded ? 'expanded' : ''}`}>
              <i className="pe-7s-angle-right"></i>
            </i>
          ) : (
            <i className="mobile-menu-arrow-empty">&nbsp;</i>
          )}
        </a>
        {hasChildren && (
          <ul
            className={`mobile-menu-submenu ${isExpanded ? 'expanded' : ''}`}
            style={{ display: isExpanded ? 'block' : 'none' }}
          >
            {item.children.map((child, childIndex) =>
              renderMenuItem(child, childIndex, level + 1, currentPath)
            )}
          </ul>
        )}
      </li>
    );
  }, [navigate, onClose, convertToReactRoute, expandedPaths, activeMenuPaths, toggleMenu]);

  const handleLogoutClick = useCallback(
    async (e) => {
      e.preventDefault();
      const result = await openConfirm({
        title: t('headerTop.logoutConfirmTitle'),
        content: t('headerTop.logoutConfirmMessage'),
      });
      if (result?.submit) {
        await logout();
        onClose?.();
        navigate('/login', { replace: true });
      }
    },
    [openConfirm, logout, navigate, onClose, t],
  );

  const mobileMenuContent = (
    <div
      ref={mobileMenuRef}
      className={`off-canvas-active mobile-menu-panel mobile-menu--${mobileMenuStyle}`}
      style={{ '--mobile-menu-accent': themeColor }}
    >
      <div className="off-canvas-wrap">
        <div className="mobile-menu-header">
          <div className="mobile-menu-header-top">
            <div className="mobile-menu-header-spacer"></div>
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <a
              className="off-canvas-close"
              href="#"
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (onClose) {
                  onClose();
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  if (onClose) {
                    onClose();
                  }
                }
              }}
              aria-label="메뉴 닫기"
            >
              <i className="ti-close"></i>
            </a>
          </div>
          <div className="mobile-menu-profile off-canvas-margin-padding">
            {isAuthenticated && user ? (
              <div className="mobile-profile-content">
                <div
                  className="mobile-profile-image"
                  style={{ backgroundColor: themeColor }}
                >
                  {profileAvatarSrc && !profileAvatarError ? (
                    <img
                      src={profileAvatarSrc}
                      alt=""
                      onError={() => setProfileAvatarError(true)}
                    />
                  ) : null}
                  <div
                    className="mobile-profile-initial"
                    style={{
                      display: profileAvatarSrc && !profileAvatarError ? 'none' : 'flex'
                    }}
                  >
                    {(user.userNm || user.userName || user.userId || 'U').charAt(0).toUpperCase()}
                  </div>
                </div>
                <div className="mobile-profile-info">
                  <div className="mobile-profile-name">
                    {user.userNm || user.userName || user.userId || '사용자'}
                    {user.userId ? `(${user.userId})` : ''}
                  </div>
                  {user.companyName != null && String(user.companyName).trim() !== '' ? (
                    <div className="mobile-profile-org">{String(user.companyName).trim()}</div>
                  ) : null}
                </div>
              </div>
            ) : (
              <div className="mobile-profile-content">
                <div
                  className="mobile-profile-image"
                  style={{ backgroundColor: themeColor }}
                >
                  <div className="mobile-profile-initial" style={{ display: 'flex' }}>
                    G
                  </div>
                </div>
                <div className="mobile-profile-info">
                  <div className="mobile-profile-name">{t('mobileMenu.guest')}</div>
                  <div className="mobile-profile-guest-row">
                    <span className="mobile-profile-guest-hint">{t('mobileMenu.loginRequired')}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="mobile-menu-content">
        <div className="mobile-menu-wrap off-canvas-margin-padding-2">
          <div
            id="mobile-menu"
            className="mobile-menu-tree text-left"
          >
            {menuData && menuData.length > 0 ? (
              <ul>
                {menuData.map((item, index) => renderMenuItem(item, index, 0, []))}
              </ul>
            ) : (
              <ul></ul>
            )}
          </div>
        </div>
        <div className="mobile-menu-footer off-canvas-margin-padding-2">
          <div className="mobile-menu-footer-auth">
            {isAuthenticated ? (
              <button type="button" className="mobile-menu-logout-btn" onClick={handleLogoutClick}>
                {t('headerTop.logout')}
              </button>
            ) : (
              <>
                <Link
                  to="/login"
                  className="mobile-menu-auth-btn mobile-menu-auth-btn--primary"
                  onClick={() => onClose && onClose()}
                >
                  {t('login.submit')}
                </Link>
                <Link
                  to="/register"
                  className="mobile-menu-auth-btn mobile-menu-auth-btn--secondary"
                  onClick={() => onClose && onClose()}
                >
                  {t('login.signup')}
                </Link>
              </>
            )}
          </div>
          <div className="mobile-menu-footer-links">
            <HeaderLanguageSwitcher
              textColor="#374151"
              menuPlacement="up"
              className="mobile-menu-lang-combo"
            />
            <span className="mobile-menu-footer-divider" aria-hidden="true">
              |
            </span>
            <Link
              to="/support/support-center/inquiry"
              className="mobile-menu-inquiry-link"
              onClick={() => onClose && onClose()}
            >
              {t('mobileMenu.inquiry')}
            </Link>
          </div>
        </div>
        </div>
      </div>
    </div>
  );

  // body 직하위에 렌더링 (React Portal 사용)
  return createPortal(mobileMenuContent, document.body);
};

export default MobileMenu;
